import type { BuilderCommunityMapConfig } from "api-types/builders/salesConfig";
import type { LotSpec } from "api-types/lots/lotTypes";

const DEFAULT_SPEC_LABEL = "Spec";
const DEFAULT_MODEL_LABEL = "Model";
const DEFAULT_STANDARD_LABEL = "Standard";

const TRUNCATE_LENGTH = 5;

/**
 * Resolves the display label for a lot spec value, using custom names from builder config
 * when set, otherwise falling back to the defaults "Spec", "Model", or "Standard".
 */
export function getLotSpecLabel(spec: LotSpec, communityMapConfig?: BuilderCommunityMapConfig): string {
  if (spec === "spec") {
    return communityMapConfig?.specHomeName?.trim() || DEFAULT_SPEC_LABEL;
  }
  if (spec === "model") {
    return communityMapConfig?.modelHomeName?.trim() || DEFAULT_MODEL_LABEL;
  }
  return DEFAULT_STANDARD_LABEL;
}

/**
 * Returns the label for the lots table badge, truncated to 5 characters with an ellipsis
 * when the label is longer. Callers should render a tooltip with `fullLabel` when `isTruncated`
 * is true.
 */
export function getTruncatedLotSpecLabel(
  spec: LotSpec,
  communityMapConfig?: BuilderCommunityMapConfig,
): { label: string; isTruncated: boolean; fullLabel: string } {
  const fullLabel = getLotSpecLabel(spec, communityMapConfig);
  if (fullLabel.length <= TRUNCATE_LENGTH) {
    return { label: fullLabel, isTruncated: false, fullLabel };
  }
  return { label: fullLabel.slice(0, TRUNCATE_LENGTH) + "…", isTruncated: true, fullLabel };
}

/**
 * Returns the lot type filter options with resolved display labels.
 */
export function getLotTypeFilterOptions(
  communityMapConfig?: BuilderCommunityMapConfig,
): Array<{ value: string; label: string }> {
  return [
    { value: "none", label: DEFAULT_STANDARD_LABEL },
    { value: "spec", label: getLotSpecLabel("spec", communityMapConfig) },
    { value: "model", label: getLotSpecLabel("model", communityMapConfig) },
  ];
}
